import z from "zod";

import { ErrorClassification } from "../../../sentry/classification";
import type { ErrorDetails } from "../domain";

const estimateErrorTypeValues = [
  "TASK_NOT_FOUND",
  "REQUEST_FOR_QUOTE_NOT_FOUND",
  "TASK_ATTEMPT_NOT_FOUND",
  "ESTIMATE_NOT_FOUND",
  "ESTIMATE_ID_NOT_FOUND",
  "ESTIMATE_LINE_ITEM_TYPE_NOT_FOUND",
  "ESTIMATE_LINE_ITEM_TYPE_UPSERT_FAILED",
  "ESTIMATE_DISCOUNT_NOT_FOUND",
  "ESTIMATE_COVERAGE_NOT_FOUND",
  "ESTIMATE_QA_RULE_NOT_FOUND",
  "ESTIMATE_PDF_PARSE_FAILED",
  "DAMAGEABLE_OUTSIDE_ESTIMATE_PROPERTY",
  "CONCESSION_LINE_ITEM_IN_MAPPING",
] as const;

const estimateErrorTypeSchema = z.enum(estimateErrorTypeValues);
const EstimateErrorType = estimateErrorTypeSchema.enum;
type EstimateErrorType = z.infer<typeof estimateErrorTypeSchema>;

export type EstimateErrors =
  | { errorType: typeof EstimateErrorType.TASK_NOT_FOUND; data: { projectId: number } }
  | {
      errorType: typeof EstimateErrorType.REQUEST_FOR_QUOTE_NOT_FOUND;
      data: { requestForQuoteId?: number; bidId?: number };
    }
  | {
      errorType: typeof EstimateErrorType.TASK_ATTEMPT_NOT_FOUND;
      data: { taskId: string };
    }
  | {
      errorType: typeof EstimateErrorType.ESTIMATE_NOT_FOUND;
      data: { estimateId?: string; requestForQuoteId?: number };
    }
  | {
      errorType: typeof EstimateErrorType.ESTIMATE_ID_NOT_FOUND;
      data: { taskAttempt: unknown };
    }
  | {
      errorType: typeof EstimateErrorType.ESTIMATE_LINE_ITEM_TYPE_NOT_FOUND;
      data: { lineItemCode: string; estimateId: string };
    }
  | {
      errorType: typeof EstimateErrorType.ESTIMATE_LINE_ITEM_TYPE_UPSERT_FAILED;
      data: { estimatingSystem: string; failedCount: number };
    }
  | { errorType: typeof EstimateErrorType.ESTIMATE_COVERAGE_NOT_FOUND; data: { estimateCoverageId: string } }
  | { errorType: typeof EstimateErrorType.ESTIMATE_QA_RULE_NOT_FOUND; data: { estimateQaRuleId: string } }
  | {
      errorType: typeof EstimateErrorType.ESTIMATE_DISCOUNT_NOT_FOUND;
      data: { partnerFlow: string; stateCode: string };
    }
  | {
      errorType: typeof EstimateErrorType.ESTIMATE_PDF_PARSE_FAILED;
      data: { parserErrorType: string };
    }
  | {
      errorType: typeof EstimateErrorType.DAMAGEABLE_OUTSIDE_ESTIMATE_PROPERTY;
      data: { estimateId: string; foreignDamageableIds: string[] };
    }
  | {
      errorType: typeof EstimateErrorType.CONCESSION_LINE_ITEM_IN_MAPPING;
      data: { estimateLineItemTypeId: string; concessionLineItemTypeNames: string[] };
    };

export const estimateErrorDetails: Record<EstimateErrorType, ErrorDetails> = {
  TASK_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Task not found",
  },
  REQUEST_FOR_QUOTE_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Request for quote not found",
  },
  TASK_ATTEMPT_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Task attempts not found",
  },
  ESTIMATE_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Estimate not found",
  },
  ESTIMATE_ID_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Estimate ID not found",
  },
  ESTIMATE_LINE_ITEM_TYPE_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Estimate line item type not found after upsert",
  },
  ESTIMATE_LINE_ITEM_TYPE_UPSERT_FAILED: {
    classification: ErrorClassification.DATABASE_ERROR,
    sentryDescription: "Failed to upsert one or more estimate line item types",
  },
  ESTIMATE_DISCOUNT_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "No EstimateDiscount config for partner flow + state",
  },
  ESTIMATE_COVERAGE_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Estimate coverage not found",
  },
  ESTIMATE_QA_RULE_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Estimate QA rule not found",
  },
  ESTIMATE_PDF_PARSE_FAILED: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Estimate PDF could not be parsed by the partner parser",
  },
  DAMAGEABLE_OUTSIDE_ESTIMATE_PROPERTY: {
    classification: ErrorClassification.AUTHORIZATION_ERROR,
    sentryDescription: "Scope row referenced a damageable outside the estimate's property",
  },
  CONCESSION_LINE_ITEM_IN_MAPPING: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Estimate mapping targeted a work order concession line item",
  },
};
